import React, {useContext, useState} from "react";
import Slider from "react-slick";
import Icon from "../Icon/icon";
import ResponsiveWrapper from "../_responsive-wrapper";
import {PatioContext} from "@root/context/patio-calculator";

const ArrowButton = ({ direction, onClick }) => (
    <button
        type="button"
        aria-label={direction === "left" ? "Previous" : "Next"}
        onClick={onClick}
        className="image-gallery__btn-wrapper"
    >
        <Icon icon={direction === "left" ? "chevron-left" : "chevron-right"} className="image-gallery__btn" color="currentColor" />
    </button>
);

const getDotSizeClass = (index, activeSlide) => {
    const distance = Math.abs(index - activeSlide);

    if (distance === 0) return "image-gallery__dot--active";
    if (distance === 1) return "image-gallery__dot--normal-size";
    if (distance === 2) return "image-gallery__dot--smaller-size";
    if (distance === 3) return "image-gallery__dot--smallest-size";
    if (distance >= 4) return "image-gallery__dot--hidden";
    
    return "";
};

const appendDots = (dots, activeSlide) => {
    const translateX = -((activeSlide + 0.5) * 20);
    return (
        <div>
            <div className="image-gallery__dots-container">
                <ul className="image-gallery__dots" style={{ transform: `translateX( calc( ${translateX}px + 50%) )` }}>
                    {dots.map((dot, index) => {
                        return React.cloneElement(dot, { className: `image-gallery__dot ${getDotSizeClass(index, activeSlide)}` });
                    })}
                </ul>
            </div>
        </div>
    ); 
};

const customPaging = () => (
    <button type="button" className="image-gallery__dot-button" aria-label="dot-button">
        <span className="image-gallery__dot-content" />
    </button>
);

const ImageGallery = ({
    images,
    thumbsSlider,
    slideshowSlider,
    setSlideshowSlider,
    setThumbsSlider,
    slideTo,
    measurements,
    isVariantImage,
    WarrantyBadge,
    showArrows = false,
}) => {
    const { configuration, activeCoordinate, setActiveCoordinate } = useContext(PatioContext) || {};
    const [activeSlide, setActiveSlide] = useState(0);

    const slideshowSettings = {
        dots: false,
        arrows: showArrows,
        infinite: true,
        speed: 375,
        slidesToShow: 1,
        slidesToScroll: 1,
        fade: true,
        adaptiveHeight: true,
        prevArrow: showArrows ? <ArrowButton direction="left" /> : null,
        nextArrow: showArrows ? <ArrowButton direction="right" /> : null,
        beforeChange: (current, next) => {
            setActiveSlide(next);
        },
        responsive: [
            {
                breakpoint: 768,
                infinite: false,
                settings: { dots: false },
            },
        ],
        appendDots: (dots) => appendDots(dots, activeSlide),
        customPaging,
    };

    const gallerySettings = {
        dots: false,
        arrows: true,
        speed: 375,
        infinite: true,
        slidesToShow: images?.length <= 4 ? images.length : 4,
        slidesToScroll: 1,
        prevArrow: <ArrowButton direction="left" />,
        nextArrow: <ArrowButton direction="right" />,
    };

    const indexToLetter = (index) => String.fromCharCode(65 + index);

    return (
        <ResponsiveWrapper className="image-gallery">
            <Slider
                className="image-gallery__main"
                asNavFor={thumbsSlider}
                ref={(slider1) => setSlideshowSlider(slider1)}
                {...slideshowSettings}
            >
                {images?.map((image, imageIdx) => (
                    <div className="image-gallery__large-image-ratio" key={image?.node?.url}>
                        {imageIdx === 0 && measurements?.map((measurement, index) => {
                            const { key, x, y } = measurement;

                            if(!x || !y) return null;

                            return (
                                <div
                                    key={key}
                                    className={`image-gallery__coordinate ${activeCoordinate === key ? "image-gallery__coordinate--active" : ""}`}
                                    style={{"--x": x, "--y": y }}
                                    onClick={() => setActiveCoordinate(key)}
                                >
                                    <div className="image-gallery__coordinate-dot">{indexToLetter(index)}</div>
                                    <div className="image-gallery__coordinate-tooltip">{configuration[key]}″</div>
                                </div>
                            );
                        })}
                        {WarrantyBadge && isVariantImage(image?.node ?? image) && WarrantyBadge() ? (
                            <WarrantyBadge className="image-gallery-slideshow__item-badge" />
                        ) : null}
                        <img className="image-gallery__large-image" alt={image?.title} src={image?.src} />
                    </div>
                ))}
            </Slider>
            <Slider
                className="image-gallery__carousel"
                asNavFor={slideshowSlider}
                ref={(slider2) => setThumbsSlider(slider2)}
                {...gallerySettings}
            >
                {images?.map((image, index) => (
                    <div className="image-gallery__thumbnail">
                        <div className="image-gallery__thumbnail-ratio">
                            <button
                                type="button"
                                onClick={() => slideTo(index)}
                            >
                                <img className="image-gallery__thumbnail-image" alt={image?.title} src={image?.src} />
                            </button>
                        </div>
                    </div>
                ))}
            </Slider>
        </ResponsiveWrapper>
    );
};

export default ImageGallery;
